import React, { useState, useEffect } from 'react';
import { apiPost, apiGet } from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  Brain, Target, Shield, AlertTriangle, TrendingUp, BarChart3,
  ChevronDown, ChevronUp, Loader2, ArrowRight, ArrowLeft,
  Lightbulb, Zap, CheckCircle2, XCircle, Clock, HelpCircle,
  FileText, Compass, Activity,
} from 'lucide-react';

function ConfidenceBar({ score, label }) {
  const color = score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground w-28 shrink-0">{label}</span>
      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs font-mono font-bold w-8 text-right">{score}%</span>
    </div>
  );
}

function Section({ icon: Icon, title, color, defaultOpen, children }) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-4 hover:bg-muted/30 transition-colors text-left">
        <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center shrink-0`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="font-semibold text-sm flex-1">{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && <div className="border-t border-border p-4">{children}</div>}
    </div>
  );
}

function SeverityBadge({ level }) {
  const cls = level === 'High' ? 'bg-red-500/10 text-red-500' : level === 'Medium' ? 'bg-amber-500/10 text-amber-500' : 'bg-blue-500/10 text-blue-500';
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${cls}`}>{level}</span>;
}


// ═══════════════════════════════════════
// RECOMMENDATION GENERATOR (Input Form)
// ═══════════════════════════════════════

export function RecommendationGenerator({ onComplete, onBack }) {
  const [challenge, setChallenge] = useState('');
  const [context, setContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [step, setStep] = useState('');

  // Poll job status
  useEffect(() => {
    if (!jobId) return;
    const interval = setInterval(async () => {
      try {
        const res = await apiGet(`/intelligence/recommend/jobs/${jobId}`);
        if (res.ok) {
          const job = await res.json();
          setStep(job.progress || job.step || '');
          if (job.status === 'completed' || job.status === 'complete') {
            clearInterval(interval);
            setGenerating(false);
            onComplete(job);
          } else if (job.status === 'failed') {
            clearInterval(interval);
            setGenerating(false);
            toast.error(job.progress || job.step || 'Recommendation failed');
          }
        }
      } catch { /* retry */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [jobId, onComplete]);

  const handleGenerate = async () => {
    if (challenge.trim().length < 10) { toast.error('Describe your challenge in more detail'); return; }
    setGenerating(true);
    try {
      const res = await apiPost('/intelligence/recommend', { challenge: challenge.trim(), context: context.trim() || undefined });
      if (res.ok) {
        const data = await res.json();
        setJobId(data.job_id);
        setStep('Starting reasoning pipeline...');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to start');
        setGenerating(false);
      }
    } catch {
      toast.error('Connection error');
      setGenerating(false);
    }
  };

  if (generating) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center" data-testid="recommendation-generating">
        <div className="w-16 h-16 rounded-2xl bg-odapto-orange/10 flex items-center justify-center mx-auto mb-5">
          <Brain className="w-8 h-8 text-odapto-orange animate-pulse" />
        </div>
        <h2 className="text-xl font-bold mb-2">Generating Executive Recommendation</h2>
        <p className="text-muted-foreground text-sm mb-6">Running the 15-step strategic reasoning pipeline...</p>
        <div className="flex items-center justify-center gap-2 text-sm text-odapto-orange mb-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>{step}</span>
        </div>
        <div className="flex justify-center gap-1.5">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="w-2 h-2 rounded-full bg-odapto-orange/30 animate-pulse" style={{ animationDelay: `${i * 200}ms` }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="recommendation-form">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to modes
      </button>
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-odapto-orange/10 flex items-center justify-center mx-auto mb-4">
          <Brain className="w-7 h-7 text-odapto-orange" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Executive Strategy Recommendation</h1>
        <p className="text-muted-foreground text-sm">
          Describe your business challenge and our AI will generate a consultant-grade strategic recommendation using your Company Intelligence and the Strategic Knowledge Library.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-1.5 block">Business Challenge <span className="text-red-500">*</span></label>
          <textarea
            value={challenge}
            onChange={e => setChallenge(e.target.value)}
            placeholder="e.g., We're struggling to transition from project-based revenue to recurring SaaS revenue. Our current revenue is 80% services and we want to flip that ratio within 18 months."
            rows={4}
            className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-odapto-orange/30"
            dir="ltr"
            data-testid="challenge-input"
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-1.5 block">Additional Context <span className="text-muted-foreground text-xs">(optional)</span></label>
          <textarea
            value={context}
            onChange={e => setContext(e.target.value)}
            placeholder="Any additional context: recent events, constraints, resources, timeline pressures..."
            rows={2}
            className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-odapto-orange/30"
            dir="ltr"
            data-testid="context-input"
          />
        </div>
        <Button onClick={handleGenerate} disabled={challenge.trim().length < 10} className="w-full bg-odapto-orange hover:bg-odapto-orange-hover text-white h-12 text-base" data-testid="generate-btn">
          <Brain className="w-5 h-5 mr-2" /> Generate Executive Recommendation
        </Button>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════
// RECOMMENDATION VIEWER
// ═══════════════════════════════════════

export function RecommendationView({ data, onBack, onNewRecommendation }) {
  const rec = data?.recommendation || {};
  const domains = data?.activated_domains || [];

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="recommendation-view">
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <Button variant="outline" size="sm" onClick={onNewRecommendation}>New Recommendation</Button>
      </div>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Brain className="w-5 h-5 text-odapto-orange" />
          <span className="text-xs font-semibold text-odapto-orange uppercase tracking-wider">Executive Recommendation</span>
        </div>
        <h1 className="text-2xl font-bold mb-2">{data?.company_name}</h1>
        <p className="text-sm text-muted-foreground">{data?.challenge}</p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {domains.map(d => (
            <span key={d} className="text-[10px] px-2 py-0.5 rounded-full bg-odapto-orange/10 text-odapto-orange font-medium">{d}</span>
          ))}
        </div>
      </div>

      {/* Executive Summary */}
      <div className="p-5 rounded-xl bg-gradient-to-br from-odapto-orange/10 to-odapto-teal/10 border border-odapto-orange/20 mb-6" data-testid="exec-summary">
        <h2 className="font-bold text-base mb-2 flex items-center gap-2"><Target className="w-4 h-4 text-odapto-orange" /> Executive Summary</h2>
        <p className="text-sm leading-relaxed">{rec.executive_summary}</p>
      </div>

      {/* Confidence */}
      {rec.confidence_score != null && (
        <div className="p-4 rounded-xl border border-border bg-card mb-6" data-testid="confidence-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm">Recommendation Confidence</h3>
            <span className={`text-2xl font-bold ${rec.confidence_score >= 75 ? 'text-emerald-500' : rec.confidence_score >= 50 ? 'text-amber-500' : 'text-red-500'}`}>
              {rec.confidence_score}%
            </span>
          </div>
          {rec.confidence_breakdown && (
            <div className="space-y-2">
              <ConfidenceBar score={rec.confidence_breakdown.data_quality} label="Data Quality" />
              <ConfidenceBar score={rec.confidence_breakdown.framework_fit} label="Framework Fit" />
              <ConfidenceBar score={rec.confidence_breakdown.market_certainty} label="Market Certainty" />
              <ConfidenceBar score={rec.confidence_breakdown.execution_feasibility} label="Execution Feasibility" />
            </div>
          )}
        </div>
      )}

      {/* Sections */}
      <div className="space-y-3">
        <Section icon={Compass} title="Situation Analysis" color="bg-blue-500/10 text-blue-500" defaultOpen>
          <p className="text-sm leading-relaxed text-muted-foreground">{rec.situation_analysis}</p>
        </Section>

        <Section icon={Lightbulb} title="Root Cause Analysis" color="bg-amber-500/10 text-amber-500" defaultOpen>
          <p className="text-sm leading-relaxed text-muted-foreground">{rec.root_cause_analysis}</p>
        </Section>

        <Section icon={Target} title={`Frameworks Applied (${(rec.frameworks_applied || []).length})`} color="bg-odapto-orange/10 text-odapto-orange" defaultOpen>
          <div className="space-y-3">
            {(rec.frameworks_applied || []).map((f, i) => (
              <div key={i} className="p-3 rounded-lg bg-muted/50">
                <p className="font-semibold text-sm text-odapto-orange">{f.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{f.how_applied}</p>
                {f.insight && <p className="text-xs text-emerald-500 mt-1">Insight: {f.insight}</p>}
              </div>
            ))}
          </div>
        </Section>

        <Section icon={AlertTriangle} title={`Key Assumptions (${(rec.key_assumptions || []).length})`} color="bg-amber-500/10 text-amber-500">
          <div className="space-y-2">
            {(rec.key_assumptions || []).map((a, i) => (
              <div key={i} className="p-3 rounded-lg bg-muted/50 text-sm">
                <p className="font-medium">{a.assumption}</p>
                <p className="text-xs text-red-400 mt-0.5">If wrong: {a.risk_if_wrong}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Validation: {a.validation_method}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={Shield} title={`Risks (${(rec.risks || []).length})`} color="bg-red-500/10 text-red-500">
          <div className="space-y-2">
            {(rec.risks || []).map((r, i) => (
              <div key={i} className="p-3 rounded-lg bg-muted/50 text-sm">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium flex-1">{r.risk}</p>
                  <SeverityBadge level={r.severity} />
                </div>
                <p className="text-xs text-muted-foreground">Likelihood: {r.likelihood} | Mitigation: {r.mitigation}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={Zap} title={`Strategic Options (${(rec.strategic_options || []).length})`} color="bg-violet-500/10 text-violet-500" defaultOpen>
          <div className="space-y-3">
            {(rec.strategic_options || []).map((opt, i) => (
              <div key={i} className={`p-4 rounded-lg border ${opt.recommended ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border bg-muted/30'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {opt.recommended ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-muted-foreground" />}
                  <span className="font-bold text-sm">{opt.option}</span>
                  {opt.recommended && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500 font-medium ml-auto">Recommended</span>}
                </div>
                <p className="text-xs text-muted-foreground mb-2">{opt.description}</p>
                <div className="grid grid-cols-3 gap-2 text-center text-xs mb-2">
                  <div className="p-1.5 rounded bg-muted/50"><p className="font-bold">{opt.impact_score}/10</p><p className="text-muted-foreground text-[10px]">Impact</p></div>
                  <div className="p-1.5 rounded bg-muted/50"><p className="font-bold">{opt.feasibility_score}/10</p><p className="text-muted-foreground text-[10px]">Feasibility</p></div>
                  <div className="p-1.5 rounded bg-muted/50"><p className="font-bold">{opt.time_to_value}</p><p className="text-muted-foreground text-[10px]">Time</p></div>
                </div>
                <div className="flex gap-4 text-xs">
                  <div className="flex-1"><span className="text-emerald-500 font-medium">Pros:</span> {(opt.pros || []).join(", ")}</div>
                  <div className="flex-1"><span className="text-red-400 font-medium">Cons:</span> {(opt.cons || []).join(", ")}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={CheckCircle2} title="Recommended Action" color="bg-emerald-500/10 text-emerald-500" defaultOpen>
          {rec.recommended_action && (
            <div className="space-y-2 text-sm">
              <div><span className="font-semibold">What:</span> <span className="text-muted-foreground">{rec.recommended_action.what}</span></div>
              <div><span className="font-semibold">Why:</span> <span className="text-muted-foreground">{rec.recommended_action.why}</span></div>
              <div><span className="font-semibold">How:</span> <span className="text-muted-foreground">{rec.recommended_action.how}</span></div>
              <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                <span className="font-semibold text-emerald-500">Expected Impact:</span> <span className="text-muted-foreground">{rec.recommended_action.expected_impact}</span>
              </div>
            </div>
          )}
        </Section>

        <Section icon={BarChart3} title={`KPIs to Track (${(rec.kpis_to_track || []).length})`} color="bg-blue-500/10 text-blue-500">
          <div className="space-y-2">
            {(rec.kpis_to_track || []).map((kpi, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 text-sm">
                <Activity className="w-4 h-4 text-blue-500 shrink-0" />
                <div className="flex-1">
                  <p className="font-medium">{kpi.kpi}</p>
                  <p className="text-xs text-muted-foreground">{kpi.why}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-medium">{kpi.target}</p>
                  <p className="text-[10px] text-muted-foreground">{kpi.frequency}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={FileText} title={`Execution Roadmap (${(rec.execution_roadmap || []).length} phases)`} color="bg-odapto-teal/10 text-odapto-teal" defaultOpen>
          <div className="space-y-3">
            {(rec.execution_roadmap || []).map((phase, i) => (
              <div key={i} className="relative pl-6 pb-4 last:pb-0">
                <div className="absolute left-0 top-1 w-3 h-3 rounded-full bg-odapto-teal border-2 border-background" />
                {i < (rec.execution_roadmap || []).length - 1 && <div className="absolute left-[5px] top-4 bottom-0 w-0.5 bg-border" />}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-sm">{phase.phase}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{phase.timeline}</span>
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-0.5 mb-1">
                    {(phase.actions || []).map((a, j) => <li key={j}>• {a}</li>)}
                  </ul>
                  <p className="text-xs text-odapto-teal">Milestone: {phase.milestone}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={HelpCircle} title={`Follow-up Questions (${(rec.follow_up_questions || []).length})`} color="bg-violet-500/10 text-violet-500">
          <div className="space-y-2">
            {(rec.follow_up_questions || []).map((q, i) => (
              <div key={i} className="p-3 rounded-lg bg-muted/50 text-sm">
                <p className="font-medium">{q.question}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{q.purpose}</p>
                <p className="text-xs text-odapto-orange mt-0.5">Impact: {q.impact_on_confidence}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}
